import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { AnyAction } from 'redux';
import type { ThunkDispatch } from 'redux-thunk';
import { resolveAbandonedCartAttribution } from '../../../utils/abandonedCartAttribution';
import { restoreAbandonedCartAttribution } from '../../../actions/v3Order/v3Order';

interface RecoveryState {
  search: string | undefined;
  resolving: boolean;
  error: boolean;
  restored: boolean;
}

const hasRecoveryTarget = (search: string | undefined): boolean => {
  const target = resolveAbandonedCartAttribution(search, { consume: false });
  return Boolean(target.cartUuid || target.abandonedCartNotificationId);
};

// Restores the saved cart named by an abandoned-cart reminder link (from the
// URL, or from the stash that survived login). With no reminder target the
// state starts resolved, so an ordinary cart visit renders the cart on the
// first render exactly as before.
export default function useAbandonedCartRecovery(
  search: string | undefined,
): RecoveryState {
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, AnyAction>>();
  const [state, setState] = useState<RecoveryState>(() => ({
    search,
    resolving: hasRecoveryTarget(search),
    error: false,
    restored: false,
  }));
  useEffect(() => {
    // App's auth refresh can remount this route while the request is pending.
    // Keep its same-tab target until the live request has installed the cart.
    const target = resolveAbandonedCartAttribution(search, { consume: false });
    let current = true;
    const controller = new AbortController();
    if (!target.cartUuid && !target.abandonedCartNotificationId) {
      // Returning the same state object lets React skip the re-render.
      setState((previous) =>
        previous.search === search &&
        !previous.resolving &&
        !previous.error &&
        !previous.restored
          ? previous
          : { search, resolving: false, error: false, restored: false },
      );
      return undefined;
    }
    setState({ search, resolving: true, error: false, restored: false });
    const run = async (): Promise<void> => {
      try {
        const restored = await dispatch(
          restoreAbandonedCartAttribution(
            target,
            () => current,
            controller.signal,
          ),
        );
        if (current) {
          // The attempt is over either way. A failed one must not leave the
          // stash behind to capture later cart visits and logins in this tab.
          resolveAbandonedCartAttribution(search);
          setState({
            search,
            resolving: false,
            error: !restored,
            restored: Boolean(restored),
          });
        }
      } catch {
        if (current) {
          resolveAbandonedCartAttribution(search);
          setState({ search, resolving: false, error: true, restored: false });
        }
      }
    };
    void run();
    return () => {
      current = false;
      controller.abort();
    };
  }, [dispatch, search]);
  return state.search === search
    ? state
    : {
        search,
        resolving: hasRecoveryTarget(search),
        error: false,
        restored: false,
      };
}
